/**
 * Makes React survive a DOM it no longer owns.
 *
 * A third party that moves or replaces a node React is still tracking makes the commit-phase
 * `removeChild`/`insertBefore` throw `NotFoundError`. React routes that to the nearest error
 * boundary, so one stray rewrite takes the whole app down to the crash page. Degrading the doomed
 * call instead leaves at worst a stale or out-of-order node on screen - never a missing one.
 *
 * `translate="no"` on both documents closes the page-translation route into this, which leaves
 * extensions that rewrite the DOM without honouring it. Nobody knows whether those are real here,
 * so the reports below are what decide whether this file earns its place: no `translated-*` in
 * `documentClass` means an extension, and nothing arriving at all means this can go.
 */

export interface DomMutationContext {
    operation: string;
    /** What the guard did instead of throwing - `skipped` or `appended`. */
    recovery: string;
    node: string;
    actualParent: string;
    expectedParent: string;
    /** Google Translate stamps `translated-ltr`/`translated-rtl` on `<html>`, which is what tells the two suspects apart. */
    documentClass: string;
    documentLang: string;
    browserLanguage: string;
}

export type DomMutationReporter = (message: string, context: DomMutationContext) => void;

let report: DomMutationReporter | null = null;

const reportedOperations = new Set<string>();

const divergenceCounts = new Map<string, number>();

const originalRemoveChild = Node.prototype.removeChild;
const originalInsertBefore = Node.prototype.insertBefore;

/**
 * Wired to the Rollbar instance in `index.jsx`; without it a skipped mutation is only a console
 * warning. Resets the tally, so a reporter registered after a divergence still gets to see the
 * first one and counts from where it started listening.
 */
export function setDomMutationReporter(reporter: DomMutationReporter | null): void {
    report = reporter;
    reportedOperations.clear();
    divergenceCounts.clear();
}

// deliberately no text content: a tooltip or a grid cell carries customer data, and the node's identity is the diagnostic part
function describe(node: Node | null): string {
    if (!node) {
        return 'none';
    }

    if (!(node instanceof Element)) {
        return node.nodeName;
    }

    const classes = node.getAttribute('class')?.trim();

    return node.nodeName.toLowerCase()
        + (node.id ? `#${node.id}` : '')
        + (classes ? `.${classes.split(/\s+/).join('.')}` : '');
}

function reportDivergence(operation: string, recovery: string, node: Node, expectedParent: Node): void {
    const count = (divergenceCounts.get(operation) ?? 0) + 1;
    divergenceCounts.set(operation, count);

    const message = `DOM guard: ${operation} target is no longer a child of the parent React expects`;
    const context: DomMutationContext = {
        operation,
        recovery,
        node: describe(node),
        actualParent: describe(node.parentNode),
        expectedParent: describe(expectedParent),
        documentClass: document.documentElement.className,
        documentLang: document.documentElement.lang,
        browserLanguage: navigator.language,
    };

    // identifiers rather than the nodes: Rollbar keeps console calls as telemetry, and a DOM node serialises to `{}`
    console.warn(message, {...context, occurrence: count});

    // only the first of each: whatever rewrote the DOM keeps doing it on every re-render, and the rest would eat the Rollbar rate limit
    if (report && !reportedOperations.has(operation)) {
        reportedOperations.add(operation);
        report(message, context);
    }
}

Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
        reportDivergence('removeChild', 'skipped', child, this);
        return child;
    }

    return originalRemoveChild.call(this, child);
};

Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
        reportDivergence('insertBefore', 'appended', child, this);
        // appended rather than dropped: a missing node is worse than one out of order, and it keeps the node where React expects to find it
        return originalInsertBefore.call(this, node, null);
    }

    return originalInsertBefore.call(this, node, child);
};
